import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { SvgIcon } from '@material-ui/core';
import { useCallback, useState } from 'react';
import { useTheme } from '../../themes';
import { setDisplayName } from '../utils';
import * as c2foIcons from './icons-c2fo';
import * as liquidityIcons from './icons-liquidity';
function buildSVGIcon(component) {
    const IconComponent = (props) => {
        const { name: themeName } = useTheme();
        const Component = (themeName === 'liquidity' ? liquidityIcons : c2foIcons)[component];
        const [viewBox, setViewBox] = useState();
        // In order to support both the old and new icon sets, we need to set the viewBox manually
        // for the new icons. This is because the new icons are not all the same size.
        // Passing a callback to the SVG Component as a ref is the way to get access to the underlying DOM node
        // and then using that, we can get the viewBox attribute and set it to the SvgIcon component.
        // https://reactjs.org/docs/refs-and-the-dom.html#callback-refs
        const ref = useCallback((node) => { var _a; return setViewBox((_a = node === null || node === void 0 ? void 0 : node.getAttribute('viewBox')) !== null && _a !== void 0 ? _a : undefined); }, []);
        return (_jsx(SvgIcon, Object.assign({ style: { pointerEvents: 'none' } }, props, { component: (_a) => {
                var { viewBox: _vb } = _a, componentProps = __rest(_a, ["viewBox"]);
                return _jsx(Component, Object.assign({}, componentProps, { ref: ref }), void 0);
            }, viewBox: viewBox }), void 0));
    };
    setDisplayName(IconComponent, `${component}Icon`);
    return IconComponent;
}
export const AccountCircleIcon = buildSVGIcon('AccountCircle');
export const AccountMultipleIcon = buildSVGIcon('AccountMultiple');
export const AccountPlusIcon = buildSVGIcon('AccountPlus');
export const AddIcon = buildSVGIcon('Add');
export const AngleDoubleLeftIcon = buildSVGIcon('AngleDoubleLeft');
export const AngleDoubleRightIcon = buildSVGIcon('AngleDoubleRight');
export const AngleDownIcon = buildSVGIcon('AngleDown');
export const AngleLeftIcon = buildSVGIcon('AngleLeft');
export const AngleRightIcon = buildSVGIcon('AngleRight');
export const AngleUpIcon = buildSVGIcon('AngleUp');
export const ArrowDownIcon = buildSVGIcon('ArrowDown');
export const ArrowDropUpIcon = buildSVGIcon('SortUp');
export const ArrowLeftIcon = buildSVGIcon('ArrowLeft');
export const ArrowToLeftIcon = buildSVGIcon('ArrowToLeft');
export const ArrowRightIcon = buildSVGIcon('ArrowRight');
export const ArrowUpIcon = buildSVGIcon('ArrowUp');
export const BadgePercentIcon = buildSVGIcon('BadgePercent');
export const BanIcon = buildSVGIcon('Ban');
export const BarsIcon = buildSVGIcon('Bars');
export const BellEmptyIcon = buildSVGIcon('BellEmpty');
export const BellIcon = buildSVGIcon('Bell');
export const BellOutlinedIcon = buildSVGIcon('BellOutlined');
export const BellCogIcon = buildSVGIcon('BellCog');
export const BoltIcon = buildSVGIcon('Bolt');
export const BriefcaseIcon = buildSVGIcon('Briefcase');
export const CardsIcon = buildSVGIcon('Cards');
export const CaretDownFilledIcon = buildSVGIcon('CaretDownFilled');
export const CaretLeftFilledIcon = buildSVGIcon('CaretLeftFilled');
export const CaretRightFilledIcon = buildSVGIcon('CaretRightFilled');
export const CaretUpFilledIcon = buildSVGIcon('CaretUpFilled');
export const CalendarAltIcon = buildSVGIcon('CalendarAlt');
export const CalendarClockIcon = buildSVGIcon('CalendarClock');
export const CalendarDayIcon = buildSVGIcon('CalendarDay');
export const CalendarIcon = buildSVGIcon('Calendar');
export const CalendarQuestionIcon = buildSVGIcon('CalendarQuestion');
export const CameraIcon = buildSVGIcon('Camera');
export const ChartMixedIcon = buildSVGIcon('ChartMixed');
export const CheckCircleIcon = buildSVGIcon('CheckCircle');
export const CheckIcon = buildSVGIcon('Check');
export const CheckFilledIcon = buildSVGIcon('CheckFilled');
export const CircleIcon = buildSVGIcon('Circle');
export const ClockIcon = buildSVGIcon('Clock');
export const CloseIcon = buildSVGIcon('Times');
export const CloudUploadIcon = buildSVGIcon('CloudUpload');
export const CogIcon = buildSVGIcon('Cog');
export const CommentsAltIcon = buildSVGIcon('CommentsAlt');
export const CompareHorizontalIcon = buildSVGIcon('CompareHorizontal');
export const CopyIcon = buildSVGIcon('Copy');
export const CreditCardIcon = buildSVGIcon('CreditCard');
export const DownloadIcon = buildSVGIcon('Download');
export const EnvelopeIcon = buildSVGIcon('Envelope');
export const ExclamationCircleIcon = buildSVGIcon('ExclamationCircle');
export const ExclamationSquareIcon = buildSVGIcon('ExclamationSquare');
export const ExclamationTriangleIcon = buildSVGIcon('ExclamationTriangle');
export const EyeIcon = buildSVGIcon('Eye');
export const EyeCrossedIcon = buildSVGIcon('EyeCrossed');
export const EyeOpenIcon = buildSVGIcon('EyeOpen');
export const FileWordIcon = buildSVGIcon('FileWord');
export const FileAltIcon = buildSVGIcon('FileAlt');
export const FileSpreadsheetIcon = buildSVGIcon('FileSpreadsheet');
export const FileCsvIcon = buildSVGIcon('FileCsv');
export const FilePdfIcon = buildSVGIcon('FilePdf');
export const FilterIcon = buildSVGIcon('Filter');
export const FilterVariantIcon = buildSVGIcon('FilterVariant');
export const GlobeAsiaIcon = buildSVGIcon('GlobeAsia');
export const HashtagIcon = buildSVGIcon('Hashtag');
export const HistoryIcon = buildSVGIcon('History');
export const HourglassHalfIcon = buildSVGIcon('HourglassHalf');
export const InboxIcon = buildSVGIcon('Inbox');
export const InfoIcon = buildSVGIcon('InfoCircle');
export const InvoiceIcon = buildSVGIcon('Invoice');
export const InvoiceRollIcon = buildSVGIcon('InvoiceRoll');
export const LandmarkIcon = buildSVGIcon('Landmark');
export const ListAltIcon = buildSVGIcon('ListAlt');
export const ListViewIcon = buildSVGIcon('ListView');
export const LockAltIcon = buildSVGIcon('LockAlt');
export const LongArrowDownIcon = buildSVGIcon('LongArrowDown');
export const LongArrowUpIcon = buildSVGIcon('LongArrowUp');
export const LinkIcon = buildSVGIcon('Link');
export const LinkBrokenIcon = buildSVGIcon('LinkBroken');
export const LinkSlashIcon = buildSVGIcon('LinkSlash');
export const MessageIcon = buildSVGIcon('Message');
export const MapMarkerAltIcon = buildSVGIcon('MapMarkerAlt');
export const MobileIcon = buildSVGIcon('Mobile');
export const MoreVertIcon = buildSVGIcon('EllipsisV');
export const PencilIcon = buildSVGIcon('Pencil');
export const PenAndBillIcon = buildSVGIcon('PenAndBill');
export const PercentageIcon = buildSVGIcon('Percentage');
export const PhoneOfficeIcon = buildSVGIcon('PhoneOffice');
export const PhoneIcon = buildSVGIcon('Phone');
export const QuestionCircleIcon = buildSVGIcon('QuestionCircle');
export const QuotesIcon = buildSVGIcon('Quotes');
export const RepeatingIcon = buildSVGIcon('Repeat');
export const RotateBillIcon = buildSVGIcon('RotateBill');
export const SearchIcon = buildSVGIcon('Search');
export const SortAmountDownIcon = buildSVGIcon('SortAmountDown');
export const SortAmountUpIcon = buildSVGIcon('SortAmountUp');
export const SpedometerIcon = buildSVGIcon('Spedometer');
export const StackBillsIcon = buildSVGIcon('StackBills');
export const StackMoneyIcon = buildSVGIcon('StackMoney');
export const StarIcon = buildSVGIcon('Star');
export const SyncIcon = buildSVGIcon('Sync');
export const TagIcon = buildSVGIcon('Tag');
export const TimesCircleIcon = buildSVGIcon('TimesCircle');
export const TrashIcon = buildSVGIcon('TrashAlt');
export const TrashListIcon = buildSVGIcon('TrashList');
export const TrendingUpIcon = buildSVGIcon('TrendingUp');
export const UploadIcon = buildSVGIcon('Upload');
export const UploadImageIcon = buildSVGIcon('UploadImage');
export const UserCircleIcon = buildSVGIcon('UserCircle');
export const TodayIcon = buildSVGIcon('Today');
export const MonetizationOnIcon = buildSVGIcon('MonetizationOn');
export const LanguageIcon = buildSVGIcon('Language');
export const MoneyBillIcon = buildSVGIcon('MoneyBill');
export const WhatsappIcon = buildSVGIcon('Whatsapp');
export const WalletIcon = buildSVGIcon('Wallet');
export const KeyIcon = buildSVGIcon('Key');
export const PaperBillsIcon = buildSVGIcon('PaperBills');
export const PointTapIcon = buildSVGIcon('PointTap');
export const SaveChangesIcon = buildSVGIcon('SaveChanges');
export const GraphIcon = buildSVGIcon('Graph');
export const TechnicalDifficultyIcon = buildSVGIcon('TechnicalDifficulty');
export const ClipboardClockIcon = buildSVGIcon('ClipboardClock');
export const CheckRegularCircleIcon = buildSVGIcon('CheckRegularCircle');
export const XmarkFilledCircleIcon = buildSVGIcon('XmarkFilledCircle');
export const DashedCircleIcon = buildSVGIcon('DashedCircle');
//# sourceMappingURL=Icon.js.map